import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { booleanRenderPropertySelector, getGalleryIdSelector } from '~/client/store/config'
import { LOCAL_STORAGE_KEYS } from '~/client/constants'
import { useBrandContext } from '@vp/react-brand'
import { RenderableTextField, SelectedAsset } from '../store/types'
import { useSchemesStore } from '../store/SchemesProvider'
import { RenderProperty } from 'shared/renderProperties'

export interface SchemeActions {
  activate: () => void
  deactivate: () => void
  update: (images: SelectedAsset[], textFields: RenderableTextField[]) => Promise<void>
  schemeExists: boolean
  isActive: boolean
}

export const useSchemeActions = (schemeId: string): SchemeActions => {
  const { updateScheme, createScheme, setActiveScheme, state } = useSchemesStore()
  const galleryId = useSelector(getGalleryIdSelector)
  const brandContext = useBrandContext()
  const brandInitialized = brandContext.isInitialized
  const hasBrand = brandContext.brands.length > 0

  const booleanRenderProperty = useSelector(booleanRenderPropertySelector)
  const storeToBrand = booleanRenderProperty(RenderProperty.PersonalizationStoreToBrand)

  const storageKey = useMemo(() => LOCAL_STORAGE_KEYS.V2_PERSISTED_SESSION + schemeId + galleryId, [schemeId, galleryId])

  const activate = useCallback(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.V2_ACTIVE_SESSION + galleryId, schemeId)
    setActiveScheme(schemeId)
  }, [galleryId, schemeId, setActiveScheme])

  const deactivate = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.V2_ACTIVE_SESSION + galleryId)
    setActiveScheme(undefined)
  }, [galleryId, setActiveScheme])

  const schemeExists = !!state.schemes[schemeId]

  const update = useCallback(
    async (images: SelectedAsset[], textFields: RenderableTextField[]) => {
      if (!brandInitialized) {
        return
      }

      if (schemeExists) {
        await updateScheme({
          schemeId,
          images,
          textFields,
          shouldUpdateBrand: storeToBrand,
        })
      } else {
        await createScheme({
          schemeId,
          images,
          textFields,
          storageKey,
          shouldCreateBrand: !hasBrand && storeToBrand,
        })
      }
    },
    [brandInitialized, schemeExists, updateScheme, schemeId, createScheme, storageKey, hasBrand, storeToBrand],
  )

  const isActive = schemeId === state.activeSchemeId

  return {
    activate,
    deactivate,
    update,
    schemeExists,
    isActive,
  }
}
